import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { CircleNotch, User, MagnifyingGlass } from '@phosphor-icons/react';
import api from '../services/api';

/**
 * Auto-suggest patient search component.
 * Shows a dropdown with matching patients as user types.
 * 
 * Props:
 *   onSelect(patient) - called when a patient is selected
 *   placeholder - input placeholder text
 *   value - controlled display value (optional)
 *   className - additional CSS classes
 *   excludeIds - array of patient IDs to exclude from results
 */
const PatientSuggest = ({ onSelect, placeholder = "Search patient by name or Medicaid ID...", value, className = "", excludeIds = [] }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/patients/suggest?q=${encodeURIComponent(q)}&limit=8`);
        const filtered = excludeIds.length > 0
          ? res.data.filter(p => !excludeIds.includes(p.id))
          : res.data;
        setResults(filtered);
        setOpen(filtered.length > 0);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const handleSelect = (patient) => {
    setQuery(`${patient.first_name} ${patient.last_name}`);
    setOpen(false);
    onSelect?.(patient);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid="patient-suggest">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-9 pr-8"
          data-testid="patient-suggest-input"
        />
        {loading && <CircleNotch size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="patient-suggest-dropdown">
          {results.map((p) => (
            <div
              key={p.id}
              onClick={() => handleSelect(p)}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-indigo-50 cursor-pointer border-b border-slate-50 last:border-0"
              data-testid={`patient-suggest-item-${p.id}`}
            >
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center shrink-0">
                <User size={14} className="text-indigo-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {p.first_name} {p.middle_name ? p.middle_name + ' ' : ''}{p.last_name}
                  {p.status === 'Inactive' && <span className="ml-2 text-xs text-amber-600">(Inactive)</span>}
                </p>
                <p className="text-xs text-slate-500 font-mono">
                  {p.medicaid_id}{p.payer_type ? ` · ${p.payer_type}` : ''}{p.city ? ` · ${p.city}` : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientSuggest;
